import React, { useState } from "react";
import { Mail, Send, Star } from "lucide-react";
import { toast } from "react-toastify";
import Utils from "../../utils";
import { decryptData } from "../../utils/crypto";
import { createContactUs } from "../../services/Front/FrontService";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    type: "support",
    subject: "",
    message: "",
    rating: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = (data) => {
    const errors = {};

    if (!data.name.trim()) {
      errors.name = "Name is required";
    }

    if (!data.email.trim()) {
      errors.email = "Email is required";
    } else {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailPattern.test(data.email)) {
        errors.email = "Please enter a valid email";
      }
    }

    if (data.type === "support" && !data.subject.trim()) {
      errors.subject = "Subject is required";
    }

    if (!data.message.trim()) {
      errors.message = "Message is required";
    }

    if (data.type === "feedback" && !data.rating) {
      errors.rating = "Please provide a rating";
    }

    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validateForm(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      ...formData,
      rating: formData.type === "feedback" ? formData.rating : null,
    };

    setErrors({});
    setIsLoading(true);
    createContactUs(payload)
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        toast.success(decrypted.message);
        setFormData({
          name: "",
          email: "",
          type: "support",
          subject: "",
          message: "",
          rating: 0,
        });

        setErrors({});
      })
      .catch((error) => {
        toast.error(Utils.getErrorMessage(error));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#f8fafc] via-white to-[#eef4ff] py-24 px-6 lg:px-16">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-300/20 blur-3xl rounded-full animate-blob"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-300/20 blur-3xl rounded-full animate-blob"></div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(15,23,42,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
        <svg
          className="absolute inset-0 w-full h-full opacity-20"
          viewBox="0 0 1400 800"
          fill="none"
        >
          <path
            d="M120 520 C320 180, 900 120, 1220 340"
            stroke="#D4AF37"
            strokeWidth="2"
            strokeDasharray="10 10"
            className="animate-route"
          />

          <g className="animate-plane">
            <text x="0" y="0" fontSize="28">
              ✈️
            </text>
          </g>
        </svg>
      </div>
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6 text-slate-primary">
            Contact Us
          </h1>

          <p className="max-w-2xl mx-auto text-slate-700 text-base sm:text-lg leading-8 font-medium">
            Have questions about reward points, travel deals, partnerships, or
            early access? We’d love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div className="bg-white/40 border border-white/50 rounded-3xl p-8 backdrop-blur-2xl shadow-[0_10px_40px_rgba(0,0,0,0.08)]">
            <h2 className="text-3xl font-black mb-8 text-slate-primary">
              Get In Touch
            </h2>

            <div className="space-y-8">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-2xl bg-white/60 flex items-center justify-center shadow-md">
                  <Mail className="text-voyage-cyan" size={24} />
                </div>

                <div>
                  <h3 className="font-black text-lg mb-1 text-slate-primary">
                    Email Support
                  </h3>

                  <p className="text-slate-700 leading-7">
                    jfpcustomersupport@gmail.com
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-10 rounded-3xl p-6 bg-gradient-to-r from-[#fff2b5] to-[#ffe082] border border-[#f1d266] shadow-lg">
              <h3 className="text-2xl font-black text-slate-primary mb-3">
                Early Access Program
              </h3>

              <p className="text-slate-700 leading-8">
                Join the next generation of smarter travel rewards. Get early
                access to reward optimization tools, redemption insights, and
                exclusive travel features.
              </p>
            </div>
          </div>

          <div className="bg-white/40 border border-white/50 rounded-3xl p-8 backdrop-blur-2xl shadow-[0_10px_40px_rgba(0,0,0,0.08)]">
            <h2 className="text-3xl font-black mb-8 text-slate-primary">
              Send a Message
            </h2>

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label className="block mb-2 font-bold text-slate-700">
                  Inquiry Type
                </label>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        type: "support",
                        rating: 0,
                      }))
                    }
                    className={`h-12 rounded-2xl border font-bold transition-all
                          ${
                            formData.type === "support"
                              ? "bg-voyage-cyan text-white border-voyage-cyan"
                              : "bg-white text-slate-700 border-slate-200"
                          }`}
                  >
                    Support
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        type: "feedback",
                        subject: "",
                      }))
                    }
                    className={`h-12 rounded-2xl border font-bold transition-all
                        ${
                          formData.type === "feedback"
                            ? "bg-[#D4AF37] text-white border-[#D4AF37]"
                            : "bg-white text-slate-700 border-slate-200"
                        }`}
                  >
                    Feedback
                  </button>
                </div>
              </div>

              <div>
                <label className="block mb-2 font-bold text-slate-700">
                  Full Name
                </label>

                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className="w-full bg-white/70 border border-white/60 rounded-2xl px-5 py-4 outline-none focus:border-voyage-cyan focus:ring-4 focus:ring-voyage-cyan/20 transition-all placeholder:text-slate-400"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block mb-2 font-bold text-slate-700">
                  Email Address
                </label>

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  className="w-full bg-white/70 border border-white/60 rounded-2xl px-5 py-4 outline-none focus:border-voyage-cyan focus:ring-4 focus:ring-voyage-cyan/20 transition-all placeholder:text-slate-400"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {formData.type === "support" && (
                <div>
                  <label className="block mb-2 font-bold text-slate-700">
                    Subject
                  </label>

                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="How can we help?"
                    className="w-full bg-white/70 border border-white/60 rounded-2xl px-5 py-4 outline-none focus:border-voyage-cyan focus:ring-4 focus:ring-voyage-cyan/20 transition-all placeholder:text-slate-400"
                  />

                  {errors.subject && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.subject}
                    </p>
                  )}
                </div>
              )}

              {formData.type === "feedback" && (
                <div>
                  <label className="block mb-3 font-bold text-slate-700">
                    Rate Your Experience
                  </label>

                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        type="button"
                        key={star}
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            rating: star,
                          }))
                        }
                      >
                        <Star
                          size={32}
                          className={`transition-all
                                ${
                                  star <= formData.rating
                                    ? "fill-yellow-400 text-yellow-400"
                                    : "text-slate-300"
                                }
                         `}
                        />
                      </button>
                    ))}
                  </div>

                  {errors.rating && (
                    <p className="text-red-500 text-sm mt-2">{errors.rating}</p>
                  )}
                </div>
              )}

              <div>
                <label className="block mb-2 font-bold text-slate-700">
                  {formData.type === "feedback"
                    ? "Feedback Message"
                    : "Support Message"}
                </label>

                <textarea
                  rows="6"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={
                    formData.type === "feedback"
                      ? "Tell us what you liked or what can be improved..."
                      : "Describe your issue..."
                  }
                  className="w-full bg-white/70 border border-white/60 rounded-2xl px-5 py-4 outline-none focus:border-voyage-cyan focus:ring-4 focus:ring-voyage-cyan/20 transition-all resize-none placeholder:text-slate-400"
                />

                {errors.message && (
                  <p className="text-red-500 text-sm mt-1">{errors.message}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-voyage-cyan to-[#f5c542] text-space-dark font-black py-4 rounded-2xl flex items-center justify-center gap-3 hover:scale-[1.02] active:scale-100 transition-all duration-300 shadow-[0_10px_30px_rgba(212,175,55,0.35)]"
              >
                <Send size={20} />
                {isLoading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactPage;
